import { type SDK_Cell, type SDK_Fonts, strings } from "@quickpoint.me/shared";
import { createContext } from "react";
import { useRecoilValue } from "recoil";
import styled from "styled-components";
import type { GridObject } from "../../lib/calculateGridRowsAndColumns";
import { startingPoint } from "../../lib/cssHelpers";
import { mainCssRules } from "../../lib/cssTemplates";
import { FilterFonts, FilterName, FilterSelector } from "../../state/filter";
import { PageIndex, PageSizeAtom } from "../../state/page";
import { ConstrainDimensionsSelector } from "../../state/ui";

interface FigureOutSpansArgs {
	currentCol: number;
	isInLandscape: boolean;
	isLastColInRow: boolean;
	maxCols: number;
	colSpan?: number;
}

export const IsInFlexBoxContext = createContext(false);

function figureOutSpans({
	currentCol,
	isInLandscape,
	isLastColInRow,
	maxCols,
	colSpan,
}: FigureOutSpansArgs) {
	if (colSpan) {
		return `${currentCol} / span ${colSpan}`;
	}
	if (isLastColInRow && maxCols > currentCol && isInLandscape) {
		return `${currentCol}  /  ${maxCols + 1}`;
	}
	return currentCol;
}

const StyledCell = styled.div<{
	$alignCellAsColumn: boolean;
	$baseFontSize: number;
	$baseFontSizeRaw: number;
	$column: number;
	$filter: SDK_Cell;
	$fonts: SDK_Fonts;
	$inEditMode: boolean;
	$constrainDimensions: boolean;
	$isInLandscape: boolean;
	$isPreviewer: boolean;
	$isSplitPageChildCell?: boolean;
	$isSplitCell?: boolean;
	$lastColInRow: boolean;
	$lastRowInGrid: boolean;
	$maxCols: number;
	$maxRows: number;
	$row: number;
	$isSpacer?: boolean;
	$colSpan?: number;
	$forceSingleColumn: boolean;
}>`
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  justify-content: ${(props) => props.$forceSingleColumn ? "flex-start" : startingPoint(props.theme.startingPoint)} ;
  position: relative;
  min-height: 0;
  max-height: 100%;
  grid-column: ${(props) => {
		const args = {
			currentCol: props.$column,
			isInLandscape: props.$isInLandscape,
			isLastColInRow: props.$lastColInRow,
			maxCols: props.$maxCols,
			colSpan: props.$colSpan,
		};
		return figureOutSpans(args);
	}};
  grid-row: ${(props) => props.$row};
  height: ${(props) => (props.$isSpacer ? "1em" : "auto")};
  width: ${(props) => (props.$isSplitPageChildCell && props.$isInLandscape ? "90%" : "100%")};
  --cell-background: "unset";
  --cell-background-repeat: no-repeat;
  --cell-background-position: center;
  --cell-background-size: cover;
  --cell-opacity: 1;
  --cell-blur: 0;
  &::before {
    content: '';
    width: 100%;
    height: 100%;
    position: absolute;
    visibility: visible;
    background: var(--cell-background);
    background-repeat: var(--cell-background-repeat);
    background-position: var(--cell-background-position);
    background-size: var(--cell-background-size);
    filter: blur(var(--cell-blur)) opacity(var(--cell-opacity));
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    max-width: 100%;
    max-height: 100%;
  }
  ${(props) =>
		`${mainCssRules({
			baseFontSize: props.$baseFontSize,
			baseFontSizeRaw: props.$baseFontSizeRaw,
			fonts: props.$fonts,
			theme: props.$filter,
		})}`};
  ${(props) => (props.$isSplitPageChildCell ? "padding: 0;" : "")}
  ${(props) => (props.$isSplitPageChildCell ? "justify-self: center;" : "")}
  ${(props) => (props.$isInLandscape ? "" : "padding-inline: 0;")}
`;

export const IsInCellContext = createContext(true);

function Cell({
	gridObject,
	idx,
	inEditMode,
	isPreviewer,
	alignCellAsColumn,
	isSplitPageChildCell,
}: {
	alignCellAsColumn: boolean;
	gridObject: GridObject;
	idx: number;
	inEditMode: boolean;
	isPreviewer: boolean;
	isSplitPageChildCell?: boolean;
}) {
	const filterName = useRecoilValue(FilterName);
	const filter = useRecoilValue(FilterSelector(filterName));
	const fonts = useRecoilValue(FilterFonts);
	const pageSize = useRecoilValue(PageSizeAtom);
	const constrainDimensions = useRecoilValue(ConstrainDimensionsSelector);
	const isInLandscape = pageSize.landscape;
	const baseFontSize = pageSize.baseFontSize;
	const pageIndex = useRecoilValue(PageIndex);

	return (
		<IsInFlexBoxContext.Provider value={true}>
			<IsInCellContext.Provider value={true}>
				{/* biome-ignore lint/a11y/useValidAriaRole: <explanation> */}
				<StyledCell
					$alignCellAsColumn={alignCellAsColumn}
					$baseFontSize={baseFontSize}
					$baseFontSizeRaw={pageSize.baseFontSizeRaw}
					$column={gridObject.col}
					$filter={filter.cell}
					$fonts={fonts}
					$inEditMode={inEditMode}
					$constrainDimensions={constrainDimensions}
					$isInLandscape={isInLandscape}
					$isPreviewer={isPreviewer}
					$isSplitPageChildCell={isSplitPageChildCell}
					$isSplitCell={gridObject.isSplitCell}
					$lastColInRow={gridObject.lastColInRow}
					$lastRowInGrid={gridObject.lastRowInGrid}
					$maxCols={gridObject.maxCols}
					$maxRows={gridObject.maxRows}
					$row={gridObject.row}
					$isSpacer={gridObject.isSpacer}
					$colSpan={gridObject.colSpan}
					$forceSingleColumn={pageSize.forceSingleColumn}
					className={`${strings.css.styledGridCell} `}
					id={`grid-cell${idx}-col-${gridObject.col}-row-${gridObject.row}`}
					key={`grid-cell${idx}-col-${gridObject.col}-row-${gridObject.row}-${pageIndex}`}
					role="none"
				>
					{gridObject.elArray}
				</StyledCell>
			</IsInCellContext.Provider>
		</IsInFlexBoxContext.Provider>
	);
}
export default Cell;
